import { FC, HTMLAttributes } from 'react';
import { useFormContext } from 'react-hook-form';

import { AutocompleteRenderGroupParams, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { CURRENCY_LIST, CurrencyType } from 'constants/Currencies';

import { GLOBAL_CURRENCY_NAME } from 'components/BusinessToolForm/Sections/Global/form';

import { ICurrencyAutocompleteProps } from './types';
import { AutocompleteControl } from '../Form/Autocomplete';

import styles from './CurrencyAutocomplete.module.scss';

import scssVariables from 'styles/sass-variables.module.scss';

CURRENCY_LIST.sort((a, b) => b.category.localeCompare(a.category));

const GroupHeader = styled('div')(() => ({
  position: 'sticky',
  top: '-8px',
  padding: '4px 16px',
  height: '40px',
  background: scssVariables.white,
  display: 'flex',
  alignItems: 'center',
}));

const GroupItems = styled('ul')({
  padding: '8px 0',
});

export const CurrencyAutocomplete: FC<ICurrencyAutocompleteProps> = ({ disabled = false, label = '' }) => {
  const { control, setValue } = useFormContext();

  const getOptionLabel = (option: CurrencyType | string) => {
    if (typeof option === 'string') {
      return option;
    }
    return `${option.value} ${option.label}`;
  };

  const groupBy = (option: CurrencyType | string) => {
    if (typeof option === 'string') return option;

    return option.category;
  };

  const renderOption = (props: HTMLAttributes<HTMLLIElement>, option: CurrencyType | string) => {
    if (typeof option === 'string') return option;
    return (
      <li {...props}>
        <span>
          <strong>{option.value}</strong>&emsp;{option.label}
        </span>
      </li>
    );
  };

  const renderGroup = (params: AutocompleteRenderGroupParams) => {
    const groupFirstLetter = params.group[0].toUpperCase();
    const groupName = groupFirstLetter + params.group.slice(1);

    return (
      <li key={params.key}>
        <GroupHeader>
          <Typography variant="subtitle2">{groupName}</Typography>
        </GroupHeader>
        <GroupItems>
          <Typography className={styles.option} variant="subtitle1">
            {params.children}
          </Typography>
        </GroupItems>
      </li>
    );
  };

  const onClear = () => {
    setValue(GLOBAL_CURRENCY_NAME, CURRENCY_LIST[0]);
  };

  return (
    <AutocompleteControl
      control={control}
      label={label}
      id="currency.id"
      name={GLOBAL_CURRENCY_NAME}
      groupBy={groupBy}
      options={CURRENCY_LIST}
      defaultValue={CURRENCY_LIST[0]}
      disabled={disabled}
      inputClassName={styles.input}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      renderGroup={renderGroup}
      onClear={onClear}
    />
  );
};
